import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Company, UserFormData } from '../types';
import { useSendUserInvite } from '@/core/api/queries/users';
import { useCreateUser } from '@/core/api/queries/users';
import { InviteUserDto } from '@/core/api/types';
import { useUIState } from '@/core/UIStateProvider';
import { useCompanies } from '@/core/api/queries/companies';

interface AddUserModalProps {
  showAddUser: boolean;
  setShowAddUser: (show: boolean) => void;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ showAddUser, setShowAddUser }) => {
  const companies = useCompanies();
  const companiesData = (companies.data as Company[]) || [];

  const createUser = useCreateUser();
  const sendUserInvite = useSendUserInvite();
  const { addNotification } = useUIState();
  const [newUserData, setNewUserData] = useState<UserFormData>({
    name: '',
    email: '',
    role: 'user',
    companyId: '',
  });

  const handleCreateUser = async (): Promise<void> => {
    try {
      const response = await createUser.mutateAsync(newUserData);
      await sendUserInvite.mutateAsync({
        userId: response.id,
        data: { role: newUserData.role as InviteUserDto['role'] },
      });
      addNotification('success', 'User created and invitation sent!');
      setShowAddUser(false);
      setNewUserData({ name: '', email: '', role: 'USER', companyId: '' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
      addNotification('error', errorMessage);
    }
  };

  if (!showAddUser) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Add New User</h3>
          <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="user@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
            <select
              value={newUserData.role}
              onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
            <select
              value={newUserData.companyId}
              onChange={(e) => setNewUserData({ ...newUserData, companyId: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select company</option>
              {companiesData
                .filter((company) => company.role === 'LSP')
                .map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddUser(false)}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateUser}
            disabled={!newUserData.name || !newUserData.email || !newUserData.companyId || createUser.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            {createUser.isPending && <Loader2 size={16} className="animate-spin" />}
            <span>Create & Send Invite</span>
          </button>
        </div>
      </div>
    </div>
  );
};
